import { Skeleton } from '~/components/ui/skeleton';

import { useQcBackLink } from '../../hooks/use-qc-back-link';
import type { QcRunStep } from '../../utils/qc-steps';
import { ContentPage } from '../ui-kit/content-page';
import { PageHeader } from '../ui-kit/page-header';

import { QC_ALL_STEPS } from './qc-runs-model';
import { QcStepTabs } from './qc-step-tabs';

/** The run header while the run is read: the real back link, then title and facts as bars. */
export function QcRunHeaderSkeleton() {
  const back = useQcBackLink();
  return (
    <PageHeader
      back={back}
      title={
        <>
          <span className="sr-only">Loading QC check</span>
          <Skeleton aria-hidden="true" className="inline-block h-7 w-80 max-w-full align-middle" />
        </>
      }
      meta={<Skeleton aria-hidden="true" className="h-4 w-96 max-w-full" />}
    />
  );
}

/** A step body's shape while it loads: the status callout, one wide card, then two side by side. */
export function QcStepBodySkeleton() {
  return (
    <div aria-hidden="true" className="flex flex-col gap-8">
      <Skeleton className="h-[88px] w-full rounded-2xl" />
      <Skeleton className="h-[260px] w-full rounded-2xl" />
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Skeleton className="h-[200px] w-full rounded-2xl" />
        <Skeleton className="h-[200px] w-full rounded-2xl" />
      </div>
    </div>
  );
}

/** The Set up page's shape: header, step strip, the open first section and closed rows, summary. */
function SetupSkeleton() {
  return (
    <ContentPage>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-7 w-48" />
          <Skeleton className="h-4 w-96 max-w-full" />
        </div>
        <QcStepTabs current="setup" steps={QC_ALL_STEPS} />
      </div>
      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="flex min-w-0 flex-col gap-3">
          <Skeleton className="h-[360px] w-full rounded-2xl" />
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-[68px] w-full rounded-2xl" />
          ))}
        </div>
        <Skeleton className="h-[420px] w-full rounded-2xl" />
      </div>
    </ContentPage>
  );
}

/**
 * The QC pages' Suspense fallback, shaped like the page that is loading so the swap to the real
 * page doesn't jump. The run variant is the same frame the run page shows while its run is read.
 */
export function QcPageSkeleton({
  variant,
  step,
  runId,
}: {
  variant: 'setup' | 'run';
  /** Run variant: the step in the URL, shown as the current tab. */
  step?: QcRunStep | undefined;
  /** Run variant: the run the tabs link into. */
  runId?: string | undefined;
}) {
  if (variant === 'setup') return <SetupSkeleton />;
  return (
    <ContentPage className={step === 'review' ? 'max-w-none gap-5' : 'gap-5'}>
      <QcRunHeaderSkeleton />
      <QcStepTabs
        current={step ?? 'run'}
        steps={QC_ALL_STEPS}
        runId={runId}
        done={{ setup: true }}
      />
      <QcStepBodySkeleton />
    </ContentPage>
  );
}
